import {
  PROMPT_MAX_LENGTH,
  SONG_PROMPT_MAX,
  SONG_STYLE_MAX,
  type AspectRatio,
  type GenerationKind,
  type GenerationsConfig,
  type Me,
  type PhotoMode,
} from '@trendator/shared';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api, moderationMessage, type CreateGenerationRequest } from '../api';
import type { CreateInitial } from '../App';
import { CloseIcon, ImagePlusIcon, Sparkle } from '../components/icons';
import { rub } from '../format';
import { haptic } from '../telegram';

export interface InsufficientFunds {
  request: CreateGenerationRequest;
  balance: number;
  price: number;
}

type ManualKind = Exclude<GenerationKind, 'stickers'>;

const TABS: { id: ManualKind; label: string }[] = [
  { id: 'photo', label: 'Фото' },
  { id: 'video', label: 'Видео' },
  { id: 'song', label: 'Песня' },
];

const PLACEHOLDER: Record<ManualKind, string> = {
  photo: 'Например: я на обложке журнала про путешествия, закат, плёнка',
  video: 'Например: камера медленно облетает меня, ветер в волосах, закат на крыше',
  song: 'Например: весёлая песня про кота Барсика, который ворует сосиски',
};

const VIDEO_ASPECTS = ['9:16', '1:1', '16:9'] as const;
type VideoAspect = (typeof VIDEO_ASPECTS)[number];

/** Ручной режим — docs/design/Create.dc.html. Фото, видео (Seedance) и песни (Suno). */
export function Create({
  me,
  initial,
  onClose,
  onToTrends,
  onCreated,
  onInsufficient,
}: {
  me: Me;
  initial?: CreateInitial;
  onClose: () => void;
  onToTrends: () => void;
  onCreated: (generationId: string) => void;
  onInsufficient: (data: InsufficientFunds) => void;
}) {
  const [config, setConfig] = useState<GenerationsConfig | null>(null);
  const [kind, setKind] = useState<ManualKind>(initial?.kind ?? 'photo');
  const [mode, setMode] = useState<PhotoMode>(initial?.kind === 'photo' && initial.mode ? initial.mode : 'fast');
  const [prompt, setPrompt] = useState(initial?.prompt ?? '');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(
    initial?.kind === 'photo' && initial.aspectRatio ? initial.aspectRatio : '9:16',
  );
  const [videoAspect, setVideoAspect] = useState<VideoAspect>(
    initial?.kind === 'video' && initial.aspectRatio ? initial.aspectRatio : '9:16',
  );
  const [style, setStyle] = useState(initial?.kind === 'song' ? (initial.style ?? '') : '');
  const [instrumental, setInstrumental] = useState(initial?.kind === 'song' ? Boolean(initial.instrumental) : false);
  const [twinReady, setTwinReady] = useState(false);
  const [useTwin, setUseTwin] = useState(false);
  const [reference, setReference] = useState<{ id: string; url: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .generationsConfig()
      .then(setConfig)
      .catch(() => setError('Не получилось загрузить настройки. Проверь интернет.'));
    api
      .twin()
      .then((t) => {
        if (t.status === 'ready') {
          setTwinReady(true);
          setUseTwin(true);
        }
      })
      .catch(() => {});
  }, []);

  const modes = config?.photo.modes ?? [];
  const isFree = kind === 'photo' && me.freeGenerationAvailable && config?.photo.freeMode === mode;
  const price =
    kind === 'photo' ? (modes.find((m) => m.id === mode)?.price ?? 0) : kind === 'video' ? (config?.video?.price ?? 0) : (config?.song?.price ?? 0);
  const kindEnabled = kind === 'photo' ? true : kind === 'video' ? Boolean(config?.video?.enabled) : Boolean(config?.song?.enabled);
  const promptMax = kind === 'song' ? SONG_PROMPT_MAX : PROMPT_MAX_LENGTH;
  const canSubmit = prompt.trim().length >= 3 && !busy && !uploading && Boolean(config) && kindEnabled;
  const withFace = kind !== 'song';

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (file.size > 10 * 1024 * 1024) {
      setError('Фото больше 10 МБ — выбери поменьше.');
      return;
    }
    setUploading(true);
    try {
      setReference(await api.upload(file));
      setUseTwin(false);
    } catch (err) {
      setError(
        moderationMessage(err) ?? (err instanceof ApiError && err.code === 'not_image' ? 'Нужна картинка: JPG, PNG или WebP.' : 'Не получилось загрузить фото.'),
      );
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const buildRequest = (): CreateGenerationRequest => {
    const text = prompt.trim();
    const face = useTwin ? { useTwin: true } : reference ? { referenceId: reference.id } : {};
    if (kind === 'video') return { kind: 'video', prompt: text, aspectRatio: videoAspect, ...face };
    if (kind === 'song') return { kind: 'song', prompt: text, style: style.trim(), instrumental };
    return { kind: 'photo', mode, prompt: text, aspectRatio, ...face };
  };

  const submit = async () => {
    if (!canSubmit) return;
    haptic();
    setBusy(true);
    setError(null);
    const request = buildRequest();
    try {
      const g = await api.createGeneration(request);
      onCreated(g.id);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.code === 'insufficient_funds') {
        onInsufficient({ request, balance: Number(err.data.balance ?? 0), price: Number(err.data.price ?? price) });
        return;
      }
      setError(
        moderationMessage(err) ??
          (err instanceof ApiError && err.code === 'generations_disabled'
          ? 'Генерации ещё подключаются — загляни чуть позже.'
          : err instanceof ApiError && err.code === 'twin_not_ready'
            ? 'Двойник ещё не готов — закончи его во вкладке «Двойник».'
            : 'Не получилось запустить. Попробуй ещё раз.'),
      );
    }
  };

  const toggleStyle = (s: string) => {
    haptic('select');
    const parts = style
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    const has = parts.some((p) => p.toLowerCase() === s.toLowerCase());
    const next = has ? parts.filter((p) => p.toLowerCase() !== s.toLowerCase()) : [...parts, s];
    setStyle(next.join(', ').slice(0, SONG_STYLE_MAX));
  };
  const styleOn = (s: string) => style.toLowerCase().split(',').map((p) => p.trim()).includes(s.toLowerCase());

  return (
    <div className="create">
      <header className="create__header">
        <h1 className="page-title">Создать</h1>
        <button type="button" className="glass icon-btn" aria-label="Закрыть" onClick={onClose}>
          <CloseIcon />
        </button>
      </header>

      <div role="tablist" aria-label="Что создаём" className="tabs3">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={kind === t.id}
            onClick={() => {
              haptic('select');
              setKind(t.id);
              setError(null);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <button type="button" className="glass trend-hint" onClick={onToTrends}>
        <span className="trend-hint__dot" />
        <span className="trend-hint__text">
          <span className="trend-hint__title">Проще начать с тренда</span>
          <span className="trend-hint__sub">Готовый сюжет, только выбери лицо</span>
        </span>
      </button>

      {kind === 'photo' ? (
        <fieldset className="methods">
          <legend className="methods__legend">Качество</legend>
          <div className="modes">
            {modes.map((m) => (
              <label key={m.id} className={`mode${mode === m.id ? ' mode--on' : ''}`}>
                <span className="mode__top">
                  <span className="mode__label">{m.label}</span>
                  <input
                    type="radio"
                    name="mode"
                    checked={mode === m.id}
                    onChange={() => {
                      haptic('select');
                      setMode(m.id);
                    }}
                  />
                </span>
                <span className="mode__hint">{m.hint}</span>
                <span className={`mode__price${mode === m.id ? ' mode__price--on' : ''}`}>
                  {me.freeGenerationAvailable && config?.photo.freeMode === m.id ? 'Бесплатно' : rub(m.price)}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      {kind !== 'photo' && config && !kindEnabled ? (
        <p className="create__soon" role="status">
          {kind === 'video' ? 'Видео' : 'Песни'} ещё подключаются — загляни чуть позже.
        </p>
      ) : null}

      <div className="glass-list prompt-box">
        {withFace && twinReady ? (
          <>
            <label className="glass-list__row create__twin">
              <span className="create__twin-avatar" />
              <span className="glass-list__label">С моим двойником</span>
              <input
                type="checkbox"
                checked={useTwin}
                onChange={(e) => {
                  haptic('select');
                  setUseTwin(e.target.checked);
                  if (e.target.checked) setReference(null);
                }}
              />
            </label>
            <div className="glass-list__sep create__twin-sep" />
          </>
        ) : null}
        <label htmlFor="prompt" className="prompt-box__label">
          {kind === 'song' ? 'О чём песня' : kind === 'video' ? 'Что происходит в видео' : 'Что сделать'}
        </label>
        <textarea
          id="prompt"
          rows={3}
          maxLength={promptMax}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={PLACEHOLDER[kind]}
        />
        {withFace && config?.uploads && !useTwin ? (
          <div className="prompt-box__tools">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => void pickFile(e.target.files?.[0])} />
            {reference ? (
              <span className="ref-chip">
                <img src={reference.url} alt="Фото-референс" />
                <button type="button" aria-label="Убрать фото" onClick={() => setReference(null)}>
                  <CloseIcon size={14} />
                </button>
              </span>
            ) : (
              <button
                type="button"
                className="chip-btn"
                aria-label={kind === 'video' ? 'Добавить фото, которое оживим' : 'Добавить фото-референс'}
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                <ImagePlusIcon />
                {uploading ? 'Загружаем…' : kind === 'video' ? 'Оживить фото' : 'Своё фото'}
              </button>
            )}
          </div>
        ) : null}
      </div>

      {kind === 'photo' ? (
        <fieldset className="methods">
          <legend className="methods__legend">Формат</legend>
          <div className="segments segments--4">
            {(config?.photo.aspectRatios ?? ['9:16', '1:1', '4:5', '16:9']).map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={aspectRatio === r}
                onClick={() => {
                  haptic('select');
                  setAspectRatio(r);
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}

      {kind === 'video' ? (
        <fieldset className="methods">
          <legend className="methods__legend">Формат · {config?.video?.durationSec ?? 5} секунд со звуком</legend>
          <div className="segments segments--3">
            {(config?.video?.aspectRatios ?? VIDEO_ASPECTS).map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={videoAspect === r}
                onClick={() => {
                  haptic('select');
                  setVideoAspect(r);
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}

      {kind === 'song' ? (
        <>
          <fieldset className="methods">
            <legend className="methods__legend">Стиль</legend>
            <div className="style-chips">
              {(config?.song?.styles ?? []).map((s) => (
                <button key={s} type="button" className="style-chip" aria-pressed={styleOn(s)} onClick={() => toggleStyle(s)}>
                  {s}
                </button>
              ))}
            </div>
            <input
              className="glass-list style-input"
              aria-label="Свой стиль"
              placeholder="Или свой: синтвейв, женский вокал, 90-е"
              maxLength={SONG_STYLE_MAX}
              value={style}
              onChange={(e) => setStyle(e.target.value)}
            />
          </fieldset>
          <label className="glass-list glass-list__row create__toggle">
            <span className="glass-list__label">Без слов, только музыка</span>
            <input
              type="checkbox"
              checked={instrumental}
              onChange={(e) => {
                haptic('select');
                setInstrumental(e.target.checked);
              }}
            />
          </label>
        </>
      ) : null}

      {error && (
        <p className="error-text" role="alert" style={{ marginTop: 14 }}>
          {error}
        </p>
      )}

      <div className="paybar paybar--create">
        <div className="paybar__info">
          <span className="paybar__credits">{isFree ? 'Бесплатно' : rub(price)}</span>
          <span className="paybar__sub">
            {isFree ? 'первая генерация' : 'ошибка — вернём деньги'}
          </span>
        </div>
        <button type="button" className="btn-accent paybar__btn" disabled={!canSubmit} onClick={submit}>
          <Sparkle />
          {busy ? 'Запускаем…' : 'Сгенерировать'}
        </button>
      </div>
    </div>
  );
}
